import { Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { useUserCourseProgress } from '@/services/admin/users.service'

interface UserCourseProgressProps {
  userId: string
}

export function UserCourseProgress({ userId }: UserCourseProgressProps) {
  const { data: courses, isLoading } = useUserCourseProgress(userId)

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  if (!courses || courses.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>📊 Nenhum curso em andamento</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {courses.map((course) => (
        <div key={course.courseId} className="border border-border/50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">{course.courseTitle}</h4>
              <p className="text-xs text-muted-foreground">
                Matriculado em {formatDate(course.enrolledAt)}
              </p>
            </div>
            <Badge
              variant={
                course.progressPercent === 100
                  ? 'default'
                  : course.progressPercent > 0
                    ? 'secondary'
                    : 'outline'
              }
            >
              {course.progressPercent === 100
                ? '✅ Concluído'
                : course.progressPercent > 0
                  ? '📖 Em progresso'
                  : '⏳ Não iniciado'}
            </Badge>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {course.completedEpisodes} de {course.totalEpisodes} aulas
              </span>
              <span className="font-semibold text-primary">{course.progressPercent}%</span>
            </div>
            <Progress value={course.progressPercent} className="h-2" />
          </div>

          {course.lastActivityAt && (
            <p className="text-xs text-muted-foreground">
              Última atividade: {formatDate(course.lastActivityAt)}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}
